import ApexCharts from "apexcharts";

/**
 * Expand-modal singleton.  One modal is re-used for every "View all" click.
 * It is appended to <body> once, then shown/hidden via CSS classes.
 */
let _modal = null;
let _modalChart = null;

function _ensureModal() {
  if (_modal) return _modal;
  _modal = document.createElement("div");
  _modal.id = "expand-chart-modal";
  _modal.className =
    "fixed inset-0 z-[9999] hidden items-center justify-center bg-black/60 backdrop-blur-sm transition-opacity";
  _modal.innerHTML = `
    <div class="relative w-[95vw] max-w-5xl max-h-[90vh] overflow-y-auto rounded-2xl bg-white dark:bg-gray-800 shadow-2xl p-6">
      <div class="flex items-center justify-between mb-4">
        <h2 id="expand-modal-title" class="text-lg font-bold text-gray-900 dark:text-white"></h2>
        <button id="expand-modal-close"
                class="rounded-lg p-1.5 text-gray-400 hover:bg-gray-100 hover:text-gray-900 dark:hover:bg-gray-700 dark:hover:text-white transition-colors"
                title="Close">
          <svg class="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/>
          </svg>
        </button>
      </div>
      <div id="expand-modal-subtitle" class="mb-3 text-xs text-gray-400 dark:text-gray-500"></div>
      <div id="expand-modal-body" class="w-full min-h-[300px]"></div>
    </div>`;
  document.body.appendChild(_modal);

  // Close handlers
  const closeBtn = _modal.querySelector("#expand-modal-close");
  closeBtn.addEventListener("click", _closeModal);
  _modal.addEventListener("click", (e) => {
    if (e.target === _modal) _closeModal();
  });
  document.addEventListener("keydown", (e) => {
    if (e.key === "Escape" && !_modal.classList.contains("hidden")) _closeModal();
  });
  return _modal;
}

function _closeModal() {
  if (!_modal) return;
  _modal.classList.add("hidden");
  _modal.classList.remove("flex");
  if (_modalChart) {
    _modalChart.destroy();
    _modalChart = null;
  }
}

/**
 * Show the expand modal for a chart.  Fetches expanded data from the server,
 * then renders a full ApexChart inside the modal.
 */
async function _openExpandModal(slot, title, totalKeys) {
  const modal = _ensureModal();
  const titleEl = modal.querySelector("#expand-modal-title");
  const subtitleEl = modal.querySelector("#expand-modal-subtitle");
  const bodyEl = modal.querySelector("#expand-modal-body");

  titleEl.textContent = title;
  subtitleEl.textContent = `Showing all ${totalKeys} entries`;
  bodyEl.innerHTML =
    '<p class="text-sm text-gray-400 text-center py-12 animate-pulse">Loading full data…</p>';

  // Show
  modal.classList.remove("hidden");
  modal.classList.add("flex");

  // Build the fetch URL.  We use the existing chart-data endpoint + ?expand=1.
  const params = new URLSearchParams(window.location.search);
  params.set("expand", "1");
  const url = `/analytics/v1/chart-data/${slot}/?${params.toString()}`;

  try {
    const resp = await fetch(url, { credentials: "same-origin" });
    if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
    const config = await resp.json();

    // Strip internal metadata keys before passing to ApexCharts
    delete config._expandable;
    delete config._total_keys;
    delete config._shown_keys;

    bodyEl.innerHTML = "";
    if (_modalChart) {
      _modalChart.destroy();
      _modalChart = null;
    }
    _modalChart = new ApexCharts(bodyEl, config);
    _modalChart.render();
  } catch (err) {
    console.error("expand-chart fetch failed", err);
    bodyEl.innerHTML =
      '<p class="text-sm text-red-400 text-center py-12">Failed to load data. Please try again.</p>';
  }
}

/**
 * Show the expand modal for the partners table.
 */
async function _openExpandPartnersTable() {
  const modal = _ensureModal();
  const titleEl = modal.querySelector("#expand-modal-title");
  const subtitleEl = modal.querySelector("#expand-modal-subtitle");
  const bodyEl = modal.querySelector("#expand-modal-body");

  titleEl.textContent = "Top partners by content posted";
  subtitleEl.textContent = "Showing all partners";
  bodyEl.innerHTML =
    '<p class="text-sm text-gray-400 text-center py-12 animate-pulse">Loading full data…</p>';

  modal.classList.remove("hidden");
  modal.classList.add("flex");

  try {
    const resp = await fetch("/analytics/v1/partners-table/", { credentials: "same-origin" });
    if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
    const data = await resp.json();

    let html = '<div class="overflow-x-auto"><table class="w-full text-sm">';
    html += `<thead><tr class="text-left text-xs uppercase text-gray-400 border-b border-gray-100 dark:border-gray-700">
      <th class="py-2 pr-2">#</th>
      <th class="py-2 pr-2">Partner</th>
      <th class="py-2 text-right">Events</th>
      <th class="py-2 text-right">Articles</th>
      <th class="py-2 text-right">Total</th>
    </tr></thead><tbody>`;
    data.rows.forEach((row, i) => {
      html += `<tr class="border-b border-gray-50 dark:border-gray-700/50">
        <td class="py-2 pr-2 text-gray-400">${i + 1}</td>
        <td class="py-2 pr-2 text-gray-800 dark:text-gray-100">${row.partner}</td>
        <td class="py-2 text-right text-gray-600 dark:text-gray-300">${row.events}</td>
        <td class="py-2 text-right text-gray-600 dark:text-gray-300">${row.articles}</td>
        <td class="py-2 text-right font-semibold text-gray-900 dark:text-white">${row.total}</td>
      </tr>`;
    });
    html += "</tbody></table></div>";
    bodyEl.innerHTML = html;
    subtitleEl.textContent = `Showing all ${data.rows.length} partners`;
  } catch (err) {
    console.error("expand-partners fetch failed", err);
    bodyEl.innerHTML =
      '<p class="text-sm text-red-400 text-center py-12">Failed to load data. Please try again.</p>';
  }
}

// Expose for the partners table button (inline onclick)
window._openExpandPartnersTable = _openExpandPartnersTable;

document.addEventListener("DOMContentLoaded", () => {
  document.querySelectorAll("[data-apex-chart]").forEach((el) => {
    const rawConfig = el.getAttribute("data-apex-chart");
    if (!rawConfig) return;

    let config;
    try {
      config = JSON.parse(rawConfig);
    } catch (e) {
      console.warn("analytics-charts: invalid JSON for", el.id, e);
      return;
    }

    // Extract expand metadata before rendering
    const expandable = config._expandable || false;
    const totalKeys = config._total_keys || 0;
    const shownKeys = config._shown_keys || 0;
    const slot = el.getAttribute("data-chart-slot") || "";
    const chartTitle = el.getAttribute("data-chart-title") || "";

    // Clean internal keys so ApexCharts doesn't choke
    delete config._expandable;
    delete config._total_keys;
    delete config._shown_keys;

    // Ensure no series data before rendering to avoid empty charts
    const hasData =
      Array.isArray(config.series) && config.series.length > 0
        ? config.series.some((s) =>
            Array.isArray(s.data) ? s.data.some((v) => v !== 0) : s !== 0
          )
        : false;

    if (!hasData) {
      el.innerHTML =
        '<p class="text-sm text-gray-400 text-center py-8">No data available for this period.</p>';
      return;
    }

    new ApexCharts(el, config).render();

    // Inject "View all N" button when the chart is expandable
    if (expandable && totalKeys > shownKeys && slot) {
      const wrapper = el.closest(".bg-white, .dark\\:bg-gray-800");
      if (wrapper) {
        const btn = document.createElement("button");
        btn.className =
          "mt-3 inline-flex items-center gap-1.5 text-xs font-semibold text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300 transition-colors cursor-pointer";
        btn.innerHTML = `
          <svg class="h-3.5 w-3.5" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" d="M4 8V4m0 0h4M4 4l5 5m11-1V4m0 0h-4m4 0l-5 5M4 16v4m0 0h4m-4 0l5-5m11 5v-4m0 4h-4m4 0l-5-5"/>
          </svg>
          View all ${totalKeys} entries (showing ${shownKeys})`;
        btn.addEventListener("click", () =>
          _openExpandModal(slot, chartTitle, totalKeys)
        );
        wrapper.appendChild(btn);
      }
    }
  });
});
